import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import {
  doc,
  getFirestore,
  setDoc,
  getDoc,
  addDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";


// Firebase Configuration - now uses environment variables
// Create a .env file in the root directory with your Firebase credentials
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};


const app = initializeApp(firebaseConfig);

const auth = getAuth(app);

const databaseId = process.env.REACT_APP_FIREBASE_DATABASE_ID || "siliconlogix-db";
const db = getFirestore(app, databaseId);

const storage = getStorage(app);

const updateUserDatabase = async (user, uid) => {
  if (typeof user !== "object") return;

  try {
    const docRef = doc(db, "users", uid);
    const existingDoc = await getDoc(docRef);
    const existingData = existingDoc.exists() ? existingDoc.data() : null;

    // Prepare updates
    const updates = {
      ...user,
      uid,
      updatedAt: new Date()
    };

    // Prevent privilege escalation: only allow setting admin role if caller is verified admin
    const isAuthorizedAdmin = (existingData && existingData.isAdmin === true) ||
      (auth.currentUser && isAdminEmail(auth.currentUser.email)) ||
      (updates.email && isAdminEmail(updates.email));

    if (updates.role === 'Admin' || updates.isAdmin === true) {
      if (!isAuthorizedAdmin) {
        console.warn("⚠️ Unauthorized role escalation attempt prevented for UID:", uid);
        updates.role = existingData?.role || 'Student';
        updates.isAdmin = false;
      } else {
        updates.isAdmin = true;
      }
    } else if (updates.role && updates.role !== 'Admin') {
      if (existingData?.isAdmin === true && !isAuthorizedAdmin) {
        updates.role = 'Admin';
        updates.isAdmin = true;
      } else {
        updates.isAdmin = false;
      }
    }

    // If no role is being updated, preserve existing role
    if (!updates.role) {
      updates.role = existingData?.role || 'Student';
      updates.isAdmin = existingData?.isAdmin || false;
    }

    await setDoc(docRef, updates, { merge: true });
    return true;
  } catch (err) {
    console.error("Error updating user:", err);
    return false;
  }
};

const getUserFromDatabase = async (uid) => {
  try {
    const docRef = doc(db, "users", uid);
    const result = await getDoc(docRef);

    if (!result.exists()) {
      console.warn(`⚠️ No Firestore document found for UID: ${uid}`);
      return null;
    }

    const userData = result.data();

    // Update last login timestamp
    try {
      await updateDoc(docRef, {
        lastLogin: new Date()
      });
    } catch (updateError) {
      console.warn("Could not update lastLogin:", updateError);
    }

    console.log(`✅ User data retrieved: ${userData.email || uid}`, {
      role: userData.role,
      isAdmin: userData.isAdmin
    });

    return userData;
  } catch (error) {
    console.error("❌ Error getting user from database:", error);
    return null;
  }
};

// Admin email whitelist for verified organization administrators
const ADMIN_EMAILS = [
  'admin@siliconlogix.in',
  'admin@silconlogix.com',
  'siliconlogix.vlsi@gmail.com'
  // Add more organization admin emails as needed
];

const isAdminEmail = (email) => {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
};

const uploadImage = (file, progressCallback, urlCallback, errorCallback, folderPath = "images") => {
  if (!file) {
    errorCallback("File not found");
    return;
  }

  const fileType = file.type;

  if (!fileType.includes("image")) {
    errorCallback("File must be an image");
    return;
  }

  const performUpload = (fileToUpload) => {
    // Generate a unique file name to avoid overwrite/cache issues
    const timestamp = Date.now();
    const uniqueFileName = `${timestamp}_${fileToUpload.name.replace(/\s+/g, '_')}`;
    const storageRef = ref(storage, `${folderPath}/${uniqueFileName}`);
    const task = uploadBytesResumable(storageRef, fileToUpload);

    task.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        progressCallback(progress);
      },
      (error) => {
        errorCallback(error.message);
      },
      () => {
        getDownloadURL(storageRef).then((url) => {
          urlCallback(url);
        });
      }
    );
  };

  // Only compress JPEGs/PNGs/WebPs to avoid breaking SVGs or animated GIFs
  const isCompressible = fileType.includes("jpeg") || fileType.includes("jpg") || fileType.includes("png") || fileType.includes("webp");

  // If the file is compressible and over 100KB, perform client-side compression
  if (isCompressible && file.size > 100 * 1024) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        const maxDimension = 1200;

        // Scale down if dimensions are large
        if (width > height) {
          if (width > maxDimension) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });

              // Verify if compressed file size is actually smaller
              if (compressedFile.size < file.size) {
                performUpload(compressedFile);
              } else {
                performUpload(file);
              }
            } else {
              performUpload(file);
            }
          },
          "image/jpeg",
          0.85 // 85% JPEG quality provides excellent high-res compression
        );
      };
      img.onerror = () => {
        performUpload(file);
      };
    };
    reader.onerror = () => {
      performUpload(file);
    };
  } else {
    // If it's small (<100KB), SVG, or GIF, upload original directly
    const fileSize = file.size / 1024 / 1024;
    if (fileSize > 5) {
      errorCallback("File must be smaller than 5MB");
      return;
    }
    performUpload(file);
  }
};

const addProjectInDatabase = async (project) => {
  if (typeof project !== "object") return;

  const collectionRef = collection(db, "projects");
  await addDoc(collectionRef, { ...project });
};

const updateProjectInDatabase = async (project, pid) => {
  if (typeof project !== "object") return;

  const docRef = doc(db, "projects", pid);
  await setDoc(docRef, { ...project });
};

const getAllProjects = async () => {
  return await getDocs(collection(db, "projects"));
};

const getAllProjectsForUser = async (uid) => {
  if (!uid) return;

  const collectionRef = collection(db, "projects");
  const condition = where("refUser", "==", uid);
  const dbQuery = query(collectionRef, condition);

  return await getDocs(dbQuery);
};

const deleteProject = async (pid) => {
  const docRef = doc(db, "projects", pid);
  await deleteDoc(docRef);
};

// Test Results Database Functions
const addTestResultInDatabase = async (testResult) => {
  if (typeof testResult !== "object") return;

  const collectionRef = collection(db, "testResults");
  await addDoc(collectionRef, { ...testResult });
};

const getAllTestResultsForUser = async (uid) => {
  if (!uid) return;

  try {
    const collectionRef = collection(db, "testResults");
    const condition = where("userId", "==", uid);
    const dbQuery = query(collectionRef, condition, orderBy("timestamp", "desc"));

    return await getDocs(dbQuery);
  } catch (error) {
    console.error("Error in getAllTestResultsForUser:", error);

    // Fallback: Try without orderBy if composite index doesn't exist
    console.log("Attempting fallback query without orderBy...");
    const collectionRef = collection(db, "testResults");
    const condition = where("userId", "==", uid);
    const dbQuery = query(collectionRef, condition);

    return await getDocs(dbQuery);
  }
};

// Get upcoming events for user (shows only registered or mandatory for all events)
const getUpcomingEventsForUser = async (uid) => {
  if (!uid) return [];

  try {
    const regQuery = query(collection(db, "eventRegistrations"), where("userId", "==", uid));
    const regSnapshot = await getDocs(regQuery);
    const registeredEventIds = new Set();
    regSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.eventId && data.status !== 'cancelled') {
        registeredEventIds.add(data.eventId);
      }
    });

    const collectionRef = collection(db, "events");
    const dbQuery = query(collectionRef, orderBy("date", "asc"));

    const snapshot = await getDocs(dbQuery);
    const events = [];

    snapshot.forEach((docSnap) => {
      const eventData = docSnap.data();
      const isMandatory = eventData.isMandatory === true || eventData.mandatoryForAll === true;
      const isRegistered = registeredEventIds.has(docSnap.id) || (Array.isArray(eventData.registeredUsers) && eventData.registeredUsers.includes(uid));

      if (isMandatory || isRegistered) {
        events.push({
          id: docSnap.id,
          ...eventData,
          isMandatory,
          isRegistered
        });
      }
    });

    return events;
  } catch (error) {
    console.error("Error fetching user events:", error);
    return [];
  }
};

// Get user's recent activities from test results and projects
const getUserRecentActivities = async (uid) => {
  if (!uid) return [];

  try {
    const testResultsRef = collection(db, "testResults");
    const testQuery = query(
      testResultsRef,
      where("userId", "==", uid),
      orderBy("timestamp", "desc")
    );
    const testSnapshot = await getDocs(testQuery);

    const activities = [];
    testSnapshot.forEach((doc) => {
      const data = doc.data();
      activities.push({
        id: doc.id,
        action: data.testType === 'quiz' ? 'Completed Quiz' : 'Completed Test',
        subject: data.testName || data.category || 'Practice Test',
        time: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(),
        type: 'test'
      });
    });

    // Sort by time descending
    activities.sort((a, b) => b.time - a.time);

    return activities.slice(0, 10);
  } catch (error) {
    console.error("Error fetching user activities:", error);
    return [];
  }
};

// Get user notifications
const getUserNotifications = async (uid) => {
  if (!uid) return [];

  try {
    const notificationsRef = collection(db, "notifications");
    const notifQuery = query(
      notificationsRef,
      where("userId", "==", uid),
      orderBy("createdAt", "desc")
    );

    const snapshot = await getDocs(notifQuery);
    const notifications = [];

    snapshot.forEach((doc) => {
      notifications.push({ id: doc.id, ...doc.data() });
    });

    return notifications;
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return [];
  }
};

// Get user's learning progress
const getUserLearningProgress = async (uid) => {
  if (!uid) return {};

  try {
    const progressRef = collection(db, "userProgress");
    const progressQuery = query(progressRef, where("userId", "==", uid));

    const snapshot = await getDocs(progressQuery);
    const progress = {};

    snapshot.forEach((doc) => {
      const data = doc.data();
      progress[data.courseId] = {
        percentage: data.percentage || 0,
        lastUpdated: data.lastUpdated,
        completedLessons: data.completedLessons || 0,
        totalLessons: data.totalLessons || 0
      };
    });

    return progress;
  } catch (error) {
    console.error("Error fetching learning progress:", error);
    return {};
  }
};

// Get user dashboard statistics
const getUserDashboardStats = async (uid) => {
  if (!uid) return null;

  try {
    console.log("Fetching dashboard stats for user:", uid);

    // Fetch read modules progress
    const readModulesQuery = query(
      collection(db, "readingUserProgress"),
      where("userId", "==", uid)
    );
    const readModulesSnapshot = await getDocs(readModulesQuery);

    const [projects, testResults, enrolledCourses] = await Promise.all([
      getAllProjectsForUser(uid),
      getAllTestResultsForUser(uid),
      getUserEnrolledCourses(uid)
    ]);

    console.log("Projects snapshot:", projects);
    console.log("Test results snapshot:", testResults);
    console.log("Enrolled courses snapshot:", enrolledCourses);
    console.log("Read modules snapshot:", readModulesSnapshot);

    const projectCount = projects?.size || 0;
    const testCount = testResults?.size || 0;
    const courseCount = Array.isArray(enrolledCourses) ? enrolledCourses.length : 0;
    const readModulesCount = readModulesSnapshot?.size || 0;

    console.log("Counts - Projects:", projectCount, "Tests:", testCount, "Courses:", courseCount, "Read Modules:", readModulesCount);

    // Calculate test statistics
    let totalTests = 0;
    let passedTests = 0;
    let averageScore = 0;
    let totalScore = 0;
    let certificatesEarned = 0;

    if (testResults) {
      testResults.forEach((doc) => {
        const data = doc.data();
        totalTests++;
        totalScore += (data.score || 0);
        if (data.passed || data.isPassed || (data.score >= 70)) {
          passedTests++;
        }
      });
      averageScore = totalTests > 0 ? Math.round(totalScore / totalTests) : 0;
    }

    // Calculate certificates (completed courses or high test scores)
    if (testResults) {
      const highScoreTests = new Set();
      testResults.forEach((doc) => {
        const data = doc.data();
        if (data.score >= 80 && data.category) {
          highScoreTests.add(data.category);
        }
      });
      certificatesEarned = highScoreTests.size;
    }

    const stats = {
      totalProjects: projectCount,
      totalTests: totalTests,
      totalCourses: courseCount,
      averageScore: averageScore,
      passedTests: passedTests,
      certificates: certificatesEarned,
      totalReadModules: readModulesCount
    };

    console.log("Dashboard stats calculated:", stats);
    return stats;
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);
    // Return default stats on error instead of null
    return {
      totalProjects: 0,
      totalTests: 0,
      totalCourses: 0,
      averageScore: 0,
      passedTests: 0,
      certificates: 0,
      totalReadModules: 0
    };
  }
};

// Questions Database Functions
const addQuestionsInDatabase = async (questions, category, difficulty) => {
  if (!Array.isArray(questions)) return;

  const collectionRef = collection(db, "questions");
  const batch = [];

  questions.forEach(question => {
    const questionData = {
      ...question,
      category: category,
      difficulty: difficulty,
      createdAt: new Date()
    };
    batch.push(addDoc(collectionRef, questionData));
  });

  return Promise.all(batch);
};

const getQuestionsByCategory = async (category, difficulty) => {
  const collectionRef = collection(db, "questions");
  const conditions = [
    where("category", "==", category),
    where("difficulty", "==", difficulty)
  ];
  const dbQuery = query(collectionRef, ...conditions);

  return await getDocs(dbQuery);
};

// Admin Database Functions
const getAllUsers = async (filter = 'active') => {
  const usersRef = collection(db, "users");

  // Fetch all users first (Firestore != query excludes docs without the field)
  const snapshot = await getDocs(usersRef);

  if (filter === 'deleted') {
    // Show only deleted users
    const deletedDocs = snapshot.docs.filter(doc => doc.data().deleted === true);
    return { docs: deletedDocs, empty: deletedDocs.length === 0 };
  } else if (filter === 'deactivated') {
    // Show only deactivated/suspended users (deactivated=true but NOT deleted)
    const deactivatedDocs = snapshot.docs.filter(doc => {
      const data = doc.data();
      return data.deactivated === true && data.deleted !== true;
    });
    return { docs: deactivatedDocs, empty: deactivatedDocs.length === 0 };
  } else if (filter === 'active') {
    // Show only active users (not deactivated AND not deleted)
    const activeDocs = snapshot.docs.filter(doc => {
      const data = doc.data();
      return data.deactivated !== true && data.deleted !== true;
    });
    return { docs: activeDocs, empty: activeDocs.length === 0 };
  } else {
    // Show all users
    return snapshot;
  }
};

const updateUserRole = async (userId, role) => {
  const userRef = doc(db, "users", userId);
  await setDoc(userRef, { role }, { merge: true });
};

const deleteUser = async (userId) => {
  const userRef = doc(db, "users", userId);
  await deleteDoc(userRef);
};

const restoreUser = async (userId) => {
  const userRef = doc(db, "users", userId);
  await updateDoc(userRef, {
    deleted: false,
    isActive: true,
    restoredAt: new Date(),
    deletedAt: null,
    deletedBy: null
  });
};

// Course Database Functions
const addCourseInDatabase = async (course) => {
  if (typeof course !== "object") return;

  const collectionRef = collection(db, "courseList");
  await addDoc(collectionRef, {
    ...course,
    createdAt: new Date(),
    updatedAt: new Date()
  });
};

const updateCourseInDatabase = async (course, courseId) => {
  if (typeof course !== "object") return;

  try {
    const docRef = doc(db, "courseList", courseId);
    await setDoc(docRef, {
      ...course,
      updatedAt: new Date()
    });
  } catch (e) {
    const docRef = doc(db, "courses", courseId);
    await setDoc(docRef, {
      ...course,
      updatedAt: new Date()
    });
  }
};

const getAllCourses = async () => {
  const snapshot = await getDocs(collection(db, "courseList"));
  if (!snapshot.empty) return snapshot;
  return await getDocs(collection(db, "courses"));
};

const deleteCourse = async (courseId) => {
  try {
    await deleteDoc(doc(db, "courseList", courseId));
  } catch (e) { }
  try {
    await deleteDoc(doc(db, "courses", courseId));
  } catch (e) { }
};

// Event Database Functions
const addEventInDatabase = async (event) => {
  if (typeof event !== "object") return;

  const collectionRef = collection(db, "events");
  await addDoc(collectionRef, {
    ...event,
    createdAt: new Date(),
    updatedAt: new Date()
  });
};

const updateEventInDatabase = async (event, eventId) => {
  if (typeof event !== "object") return;

  const docRef = doc(db, "events", eventId);
  await setDoc(docRef, {
    ...event,
    updatedAt: new Date()
  });
};

const getAllEvents = async () => {
  return await getDocs(
    query(collection(db, "events"), orderBy("date", "desc"))
  );
};

const deleteEvent = async (eventId) => {
  const docRef = doc(db, "events", eventId);
  await deleteDoc(docRef);
};

// Analytics Database Functions
const getAnalyticsData = async () => {
  const [usersSnapshot, coursesSnapshot, eventsSnapshot, testResultsSnapshot] = await Promise.all([
    getDocs(collection(db, "users")),
    getDocs(collection(db, "courses")),
    getDocs(collection(db, "events")),
    getDocs(collection(db, "testResults"))
  ]);

  return {
    users: usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
    courses: coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
    events: eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
    testResults: testResultsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
  };
};

// Announcement Database Functions
const addAnnouncementInDatabase = async (announcement) => {
  if (typeof announcement !== "object") return;

  const collectionRef = collection(db, "announcements");
  await addDoc(collectionRef, {
    ...announcement,
    createdAt: new Date(),
    updatedAt: new Date(),
    views: 0,
    readBy: []
  });
};

const updateAnnouncementInDatabase = async (announcement, announcementId) => {
  if (typeof announcement !== "object") return;

  const docRef = doc(db, "announcements", announcementId);
  await setDoc(docRef, {
    ...announcement,
    updatedAt: new Date()
  }, { merge: true });
};

const getAllAnnouncements = async () => {
  return await getDocs(
    query(collection(db, "announcements"), orderBy("createdAt", "desc"))
  );
};

const getFeaturedAnnouncements = async () => {
  const q = query(
    collection(db, "announcements"),
    where("isFeatured", "==", true),
    orderBy("createdAt", "desc")
  );
  return await getDocs(q);
};

const deleteAnnouncement = async (announcementId) => {
  const docRef = doc(db, "announcements", announcementId);
  await deleteDoc(docRef);
};

// Activity Database Functions
const addActivityInDatabase = async (activity) => {
  if (typeof activity !== "object") return;

  const collectionRef = collection(db, "activities");
  await addDoc(collectionRef, {
    ...activity,
    createdAt: new Date(),
    updatedAt: new Date(),
    participants: [],
    registeredCount: 0
  });
};

const updateActivityInDatabase = async (activity, activityId) => {
  if (typeof activity !== "object") return;

  const docRef = doc(db, "activities", activityId);
  await setDoc(docRef, {
    ...activity,
    updatedAt: new Date()
  }, { merge: true });
};

const getAllActivities = async () => {
  return await getDocs(
    query(collection(db, "activities"), orderBy("date", "desc"))
  );
};

const deleteActivity = async (activityId) => {
  const docRef = doc(db, "activities", activityId);
  await deleteDoc(docRef);
};

// Job Role Database Functions
const addJobRoleInDatabase = async (jobRole) => {
  if (typeof jobRole !== "object") return;

  const collectionRef = collection(db, "jobRoles");
  await addDoc(collectionRef, {
    ...jobRole,
    createdAt: new Date(),
    updatedAt: new Date(),
    applicants: [],
    applicantCount: 0
  });
};

const updateJobRoleInDatabase = async (jobRole, jobRoleId) => {
  if (typeof jobRole !== "object") return;

  const docRef = doc(db, "jobRoles", jobRoleId);
  await setDoc(docRef, {
    ...jobRole,
    updatedAt: new Date()
  }, { merge: true });
};

const getAllJobRoles = async () => {
  return await getDocs(
    query(collection(db, "jobRoles"), orderBy("posted", "desc"))
  );
};

const deleteJobRole = async (jobRoleId) => {
  const docRef = doc(db, "jobRoles", jobRoleId);
  await deleteDoc(docRef);
};

// Company Database Functions
const addCompanyInDatabase = async (company) => {
  if (typeof company !== "object") return;

  const collectionRef = collection(db, "companies");
  await addDoc(collectionRef, {
    ...company,
    createdAt: new Date(),
    updatedAt: new Date()
  });
};

const updateCompanyInDatabase = async (company, companyId) => {
  if (typeof company !== "object") return;

  const docRef = doc(db, "companies", companyId);
  await setDoc(docRef, {
    ...company,
    updatedAt: new Date()
  }, { merge: true });
};

const getAllCompanies = async () => {
  return await getDocs(collection(db, "companies"));
};

const deleteCompany = async (companyId) => {
  const docRef = doc(db, "companies", companyId);
  await deleteDoc(docRef);
};

// Gallery/Slider Database Functions
const addGalleryItemInDatabase = async (galleryItem) => {
  if (typeof galleryItem !== "object") return;

  const collectionRef = collection(db, "gallery");
  await addDoc(collectionRef, {
    ...galleryItem,
    createdAt: new Date(),
    updatedAt: new Date(),
    order: 0
  });
};

const updateGalleryItemInDatabase = async (galleryItem, galleryItemId) => {
  if (typeof galleryItem !== "object") return;

  const docRef = doc(db, "gallery", galleryItemId);
  await setDoc(docRef, {
    ...galleryItem,
    updatedAt: new Date()
  }, { merge: true });
};

const getAllGalleryItems = async () => {
  return await getDocs(
    query(collection(db, "gallery"), orderBy("order", "asc"))
  );
};

const deleteGalleryItem = async (galleryItemId) => {
  const docRef = doc(db, "gallery", galleryItemId);
  await deleteDoc(docRef);
};

// Leaderboard Functions (calculated from testResults)
const getLeaderboardData = async (period = 'all') => {
  const testResultsSnapshot = await getDocs(collection(db, "testResults"));
  const usersSnapshot = await getDocs(collection(db, "users"));

  const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  const testResults = testResultsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Calculate scores per user
  const userScores = {};
  const now = new Date();

  testResults.forEach(result => {
    const userId = result.userId;
    if (!userId) return;

    let shouldInclude = true;
    if (period === 'daily') {
      const resultDate = result.createdAt?.toDate();
      if (!resultDate || resultDate.toDateString() !== now.toDateString()) {
        shouldInclude = false;
      }
    } else if (period === 'weekly') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const resultDate = result.createdAt?.toDate();
      if (!resultDate || resultDate < weekAgo) {
        shouldInclude = false;
      }
    } else if (period === 'monthly') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const resultDate = result.createdAt?.toDate();
      if (!resultDate || resultDate < monthAgo) {
        shouldInclude = false;
      }
    }

    if (shouldInclude) {
      if (!userScores[userId]) {
        userScores[userId] = {
          userId,
          totalScore: 0,
          testCount: 0,
          lastTestDate: result.createdAt?.toDate()
        };
      }
      userScores[userId].totalScore += (result.score || 0);
      userScores[userId].testCount += 1;
      const resultDate = result.createdAt?.toDate();
      if (resultDate && (!userScores[userId].lastTestDate || resultDate > userScores[userId].lastTestDate)) {
        userScores[userId].lastTestDate = resultDate;
      }
    }
  });

  // Combine with user data and calculate rankings
  const leaderboard = Object.values(userScores)
    .map(scoreData => {
      const user = users.find(u => u.id === scoreData.userId);
      if (!user) return null;

      return {
        id: user.id,
        name: user.name || user.email?.split('@')[0] || 'User',
        score: scoreData.totalScore,
        testCount: scoreData.testCount,
        avatar: user.profileImage || user.photoURL || "/static_img/DP.jpg",
        department: user.department || user.designation || "General",
        lastTestDate: scoreData.lastTestDate,
        createdAt: user.createdAt?.toDate()
      };
    })
    .filter(item => item !== null)
    .sort((a, b) => b.score - a.score)
    .map((item, index) => ({
      ...item,
      rank: index + 1
    }));

  return leaderboard;
};

// Registered Lab Projects Functions
const registerUserForLabProject = async (uid, labProject) => {
  if (!uid || !labProject) return { success: false, error: "Missing user ID or project data" };

  try {
    // Check if already registered
    const existingRegistration = await isUserRegisteredForProject(uid, labProject.id);
    if (existingRegistration) {
      return { success: false, error: "Already registered for this project" };
    }

    const collectionRef = collection(db, "registeredProjects");
    const docRef = await addDoc(collectionRef, {
      userId: uid,
      projectId: labProject.id,
      projectTitle: labProject.title,
      projectData: labProject,
      registeredAt: new Date(),
      status: "registered",
      completionPercentage: 0
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error registering for project:", error);
    return { success: false, error: error.message };
  }
};

const getRegisteredProjectsForUser = async (uid) => {
  if (!uid) return;

  try {
    const collectionRef = collection(db, "registeredProjects");
    const condition = where("userId", "==", uid);
    const dbQuery = query(collectionRef, condition, orderBy("registeredAt", "desc"));

    return await getDocs(dbQuery);
  } catch (error) {
    console.error("Error fetching registered projects:", error);
    // Fallback without orderBy if index doesn't exist
    const collectionRef = collection(db, "registeredProjects");
    const condition = where("userId", "==", uid);
    const dbQuery = query(collectionRef, condition);
    return await getDocs(dbQuery);
  }
};

const isUserRegisteredForProject = async (uid, projectId) => {
  if (!uid || !projectId) return false;

  try {
    const collectionRef = collection(db, "registeredProjects");
    const conditions = [
      where("userId", "==", uid),
      where("projectId", "==", projectId)
    ];
    const dbQuery = query(collectionRef, ...conditions);
    const snapshot = await getDocs(dbQuery);

    return !snapshot.empty;
  } catch (error) {
    console.error("Error checking registration status:", error);
    return false;
  }
};

const updateRegisteredProjectStatus = async (docId, status, percentage) => {
  if (!docId) return;

  try {
    const docRef = doc(db, "registeredProjects", docId);
    await updateDoc(docRef, {
      status: status,
      completionPercentage: percentage,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    console.error("Error updating project status:", error);
    return { success: false, error: error.message };
  }
};

/* eslint-disable no-use-before-define */
export {
  app as default,
  auth,
  db,
  storage,
  updateUserDatabase,
  getUserFromDatabase,
  isAdminEmail,  // Admin email detection utility
  uploadImage,
  addProjectInDatabase,
  updateProjectInDatabase,
  getAllProjects,
  getAllProjectsForUser,
  deleteProject,
  addTestResultInDatabase,
  getAllTestResultsForUser,
  addQuestionsInDatabase,
  getQuestionsByCategory,
  // User Dashboard functions
  getUpcomingEventsForUser,
  getUserRecentActivities,
  getUserNotifications,
  getUserLearningProgress,
  getUserDashboardStats,
  // Admin functions
  getAllUsers,
  updateUserRole,
  deleteUser,
  restoreUser,
  // Course functions
  addCourseInDatabase,
  updateCourseInDatabase,
  getAllCourses,
  deleteCourse,
  // Event functions
  addEventInDatabase,
  updateEventInDatabase,
  getAllEvents,
  deleteEvent,
  // Analytics functions
  getAnalyticsData,
  // Announcement functions
  addAnnouncementInDatabase,
  updateAnnouncementInDatabase,
  getAllAnnouncements,
  getFeaturedAnnouncements,
  deleteAnnouncement,
  // Activity functions
  addActivityInDatabase,
  updateActivityInDatabase,
  getAllActivities,
  deleteActivity,
  // Job Role functions
  addJobRoleInDatabase,
  updateJobRoleInDatabase,
  getAllJobRoles,
  deleteJobRole,
  // Company functions
  addCompanyInDatabase,
  updateCompanyInDatabase,
  getAllCompanies,
  deleteCompany,
  // Gallery functions
  addGalleryItemInDatabase,
  updateGalleryItemInDatabase,
  getAllGalleryItems,
  deleteGalleryItem,
  // Leaderboard functions
  getLeaderboardData,
  // Contact form functions
  submitContactForm,
  getAllContactRequests,
  updateContactRequestStatus,
  // User activity tracking functions
  logUserActivity,
  startActivityTracking,
  stopActivityTracking,
  // Course enrollment functions
  enrollUserInCourse,
  checkUserEnrollment,
  getUserEnrolledCourses,
  updateCourseProgress,
  // Course Module Quiz (embedded quiz system)
  getCourseModuleQuiz,
  getCourseModuleQuizForAdmin,
  saveCourseModuleQuiz,
  getReadModuleQuizForAdmin,
  saveReadModuleQuiz,
  getReadModuleQuiz,
  saveReadModuleQuizResult,
  getReadModuleQuizResultsForUser,
  saveModuleQuizResult,
  getModuleQuizResultsForUser,
  updateModuleQuizProgressInEnrollment,
  // Registered Lab Projects functions
  registerUserForLabProject,
  getRegisteredProjectsForUser,
  isUserRegisteredForProject,
  updateRegisteredProjectStatus,
  // Notes Functions
  addNoteInDatabase,
  getNotesForUser,
  updateNoteInDatabase,
  deleteNoteFromDatabase,
  // Points System
  getUserSiliconPoints,
  getUserModuleProgress,
};
/* eslint-enable no-use-before-define */

// Module Progress Functions
const getUserModuleProgress = async (userId) => {
  if (!userId) return [];

  try {
    const progressQuery = query(
      collection(db, "userModuleProgress"),
      where("userId", "==", userId),
      orderBy("updatedAt", "desc")
    );
    const snapshot = await getDocs(progressQuery);

    if (snapshot.empty) return [];

    // Fetch module details for each progress entry
    const progressPromises = snapshot.docs.map(async (progressDoc) => {
      const progressData = progressDoc.data();
      let moduleTitle = "Unknown Module";
      let moduleColor = "#667eea"; // Default color

      try {
        if (progressData.moduleId) {
          const moduleRef = doc(db, "textModules", progressData.moduleId);
          const moduleSnap = await getDoc(moduleRef);
          if (moduleSnap.exists()) {
            moduleTitle = moduleSnap.data().title || "Untitled Module";
            // Assign random color based on title length for variety
            const colors = ["#667eea", "#f093fb", "#4facfe", "#43e97b", "#fa709a"];
            moduleColor = colors[moduleTitle.length % colors.length];
          }
        }
      } catch (err) {
        console.warn("Error fetching module details:", err);
      }

      return {
        id: progressData.moduleId,
        progressId: progressDoc.id,
        title: moduleTitle,
        percentage: progressData.completionPercentage || 0,
        completedTopics: progressData.completedTopics?.length || 0,
        lastUpdated: progressData.updatedAt?.toDate ? progressData.updatedAt.toDate() : new Date(),
        color: moduleColor
      };
    });

    return await Promise.all(progressPromises);
  } catch (error) {
    console.error("Error fetching user module progress:", error);
    return [];
  }
};

// Notes Database Functions
const addNoteInDatabase = async (userId, noteData) => {
  if (!userId || !noteData) return { success: false, error: "Missing data" };

  try {
    const collectionRef = collection(db, "notes");
    const docRef = await addDoc(collectionRef, {
      ...noteData,
      userId: userId,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error adding note:", error);
    return { success: false, error: error.message };
  }
};

const getNotesForUser = async (userId) => {
  if (!userId) return [];

  try {
    const collectionRef = collection(db, "notes");
    const q = query(
      collectionRef,
      where("userId", "==", userId),
      orderBy("updatedAt", "desc")
    );
    return await getDocs(q);
  } catch (error) {
    console.error("Error fetching notes:", error);
    // Fallback if index missing
    const collectionRef = collection(db, "notes");
    const q = query(collectionRef, where("userId", "==", userId));
    return await getDocs(q);
  }
};

const updateNoteInDatabase = async (noteId, noteData) => {
  if (!noteId) return { success: false, error: "Missing note ID" };

  try {
    const docRef = doc(db, "notes", noteId);
    await updateDoc(docRef, {
      ...noteData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    console.error("Error updating note:", error);
    return { success: false, error: error.message };
  }
};

const deleteNoteFromDatabase = async (noteId) => {
  if (!noteId) return { success: false, error: "Missing note ID" };

  try {
    const docRef = doc(db, "notes", noteId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    console.error("Error deleting note:", error);
    return { success: false, error: error.message };
  }
};


// Points System Functions
const getUserSiliconPoints = async (userId) => {
  if (!userId) return { totalPoints: 0, breakdown: {}, rank: 'Novice' };

  try {
    const [testResults, projects, readModules] = await Promise.all([
      getAllTestResultsForUser(userId),
      getAllProjectsForUser(userId),
      getDocs(query(collection(db, "userModuleProgress"), where("userId", "==", userId)))
    ]);

    let pointsFromTests = 0;
    let pointsFromProjects = 0;
    let pointsFromModules = 0;

    // Calculate Test Points (Score * 10)
    if (testResults) {
      testResults.forEach(doc => {
        const data = doc.data();
        pointsFromTests += (data.score || 0) * 10;
      });
    }

    // Calculate Project Points (50 per project)
    if (projects) {
      pointsFromProjects = (projects.size || 0) * 50;
    }

    // Calculate Module Points (20 per read module)
    if (readModules) {
      pointsFromModules = (readModules.size || 0) * 20;
    }

    const totalPoints = pointsFromTests + pointsFromProjects + pointsFromModules;

    // Determine Rank
    let rank = 'Novice';
    let icon = 'leaf';
    let color = '#aaaaaa'; // Grey

    if (totalPoints > 5000) {
      rank = 'Silicon Master';
      icon = 'crown';
      color = '#FFD700'; // Gold
    } else if (totalPoints > 2500) {
      rank = 'Expert';
      icon = 'gem';
      color = '#E5E4E2'; // Platinum
    } else if (totalPoints > 1000) {
      rank = 'Pro';
      icon = 'star';
      color = '#C0C0C0'; // Silver
    } else if (totalPoints > 500) {
      rank = 'Intermediate';
      icon = 'medal';
      color = '#CD7F32'; // Bronze
    }

    return {
      totalPoints,
      breakdown: {
        tests: pointsFromTests,
        projects: pointsFromProjects,
        modules: pointsFromModules
      },
      rank,
      rankDetails: { icon, color }
    };
  } catch (error) {
    console.error("Error calculating silicon points:", error);
    return { totalPoints: 0, breakdown: {}, rank: 'Novice' };
  }
};

// Contact form submission
const submitContactForm = async (contactData) => {
  try {
    const collectionRef = collection(db, "contactRequests");
    const docRef = await addDoc(collectionRef, {
      ...contactData,
      createdAt: new Date(),
      status: 'new',
      read: false
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error submitting contact form:", error);
    return { success: false, error: error.message };
  }
};

// Get all contact requests (for admin)
const getAllContactRequests = async () => {
  try {
    const collectionRef = collection(db, "contactRequests");
    const q = query(collectionRef, orderBy("createdAt", "desc"));
    return await getDocs(q);
  } catch (error) {
    console.error("Error fetching contact requests:", error);
    return null;
  }
};

// Update contact request status
const updateContactRequestStatus = async (requestId, status, read = true) => {
  try {
    const docRef = doc(db, "contactRequests", requestId);
    await updateDoc(docRef, { status, read, updatedAt: new Date() });
    return { success: true };
  } catch (error) {
    console.error("Error updating contact request:", error);
    return { success: false, error: error.message };
  }
};

// Log user activity (track time spent on the app)
const logUserActivity = async (userId, activeMinutes, additionalData = {}) => {
  try {
    const collectionRef = collection(db, "userActivity");
    const now = new Date();

    await addDoc(collectionRef, {
      userId,
      activeMinutes: Number(activeMinutes),
      timestamp: now,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      ...additionalData,
      createdAt: now
    });

    return { success: true };
  } catch (error) {
    console.error("Error logging user activity:", error);
    return { success: false, error: error.message };
  }
};

// Start activity tracking session
let activitySessionStart = null;
let activityCheckInterval = null;

const startActivityTracking = (userId) => {
  if (!userId) return;

  activitySessionStart = new Date();

  // Log activity every 5 minutes
  if (activityCheckInterval) {
    clearInterval(activityCheckInterval);
  }

  activityCheckInterval = setInterval(async () => {
    if (activitySessionStart) {
      const now = new Date();
      const minutesActive = Math.round((now - activitySessionStart) / 60000);

      if (minutesActive > 0) {
        await logUserActivity(userId, minutesActive, {
          sessionStart: activitySessionStart,
          sessionEnd: now,
          sessionType: 'auto'
        });

        // Reset session start
        activitySessionStart = new Date();
      }
    }
  }, 5 * 60 * 1000); // Every 5 minutes
};

const stopActivityTracking = async (userId) => {
  if (activityCheckInterval) {
    clearInterval(activityCheckInterval);
    activityCheckInterval = null;
  }

  // Log final session
  if (activitySessionStart && userId) {
    const now = new Date();
    const minutesActive = Math.round((now - activitySessionStart) / 60000);

    if (minutesActive > 0) {
      await logUserActivity(userId, minutesActive, {
        sessionStart: activitySessionStart,
        sessionEnd: now,
        sessionType: 'final'
      });
    }

    activitySessionStart = null;
  }
};

// Course Enrollment Functions
const enrollUserInCourse = async (userId, courseId) => {
  try {
    console.log("Enrolling user:", userId, "in course:", courseId);
    const enrollmentRef = collection(db, "enrollments");

    // Check if already enrolled
    const existingEnrollment = await checkUserEnrollment(userId, courseId);
    if (existingEnrollment) {
      console.log("User already enrolled in this course");
      return { success: false, message: "Already enrolled" };
    }

    // Create enrollment document
    const enrollmentData = {
      userId: userId,
      courseId: courseId,
      enrolledAt: new Date(),
      status: "active", // active, completed
      progress: 0,
      lastAccessedAt: new Date(),
      completedLessons: []
    };

    console.log("Creating enrollment with data:", enrollmentData);
    const docRef = await addDoc(enrollmentRef, enrollmentData);
    console.log("Enrollment created with ID:", docRef.id);

    // Update course enrollment count
    const courseRef = doc(db, "courses", courseId);
    const courseDoc = await getDoc(courseRef);
    if (courseDoc.exists()) {
      const currentCount = courseDoc.data().enrolledCount || 0;
      await updateDoc(courseRef, {
        enrolledCount: currentCount + 1
      });
      console.log("Updated course enrollment count to:", currentCount + 1);
    }

    return { success: true, message: "Successfully enrolled" };
  } catch (error) {
    console.error("Error enrolling user in course:", error);
    return { success: false, message: error.message };
  }
};

const checkUserEnrollment = async (userId, courseId) => {
  try {
    const enrollmentRef = collection(db, "enrollments");
    const q = query(
      enrollmentRef,
      where("userId", "==", userId),
      where("courseId", "==", courseId)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      return { enrolled: true, ...querySnapshot.docs[0].data() };
    }

    return null;
  } catch (error) {
    console.error("Error checking enrollment:", error);
    return null;
  }
};

const getUserEnrolledCourses = async (userId) => {
  try {
    console.log("Fetching enrolled courses for userId:", userId);
    const enrollmentRef = collection(db, "enrollments");
    const q = query(
      enrollmentRef,
      where("userId", "==", userId)
    );

    const enrollmentSnapshot = await getDocs(q);
    console.log("Found enrollments:", enrollmentSnapshot.size);

    const enrolledCourses = [];

    for (const enrollDoc of enrollmentSnapshot.docs) {
      const enrollmentData = enrollDoc.data();
      console.log("Processing enrollment:", enrollDoc.id, enrollmentData);

      // Fetch the actual course data
      const courseRef = doc(db, "courses", enrollmentData.courseId);
      const courseDoc = await getDoc(courseRef);

      if (courseDoc.exists()) {
        enrolledCourses.push({
          id: courseDoc.id,
          ...courseDoc.data(),
          enrollmentId: enrollDoc.id,
          enrolledAt: enrollmentData.enrolledAt,
          status: enrollmentData.status,
          progress: enrollmentData.progress,
          lastAccessedAt: enrollmentData.lastAccessedAt
        });
      } else {
        console.warn("Course not found for courseId:", enrollmentData.courseId);
      }
    }

    console.log("Returning enrolled courses:", enrolledCourses.length);
    return enrolledCourses;
  } catch (error) {
    console.error("Error fetching enrolled courses:", error);
    return [];
  }
};

const updateCourseProgress = async (userId, courseId, progress, completedLessons = []) => {
  try {
    const enrollmentRef = collection(db, "enrollments");
    const q = query(
      enrollmentRef,
      where("userId", "==", userId),
      where("courseId", "==", courseId)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const enrollmentDoc = querySnapshot.docs[0];
      const status = progress >= 100 ? "completed" : "active";

      await updateDoc(doc(db, "enrollments", enrollmentDoc.id), {
        progress: progress,
        completedLessons: completedLessons,
        lastAccessedAt: new Date(),
        status: status
      });

      return { success: true };
    }

    return { success: false, message: "Enrollment not found" };
  } catch (error) {
    console.error("Error updating course progress:", error);
    return { success: false, message: error.message };
  }
};



// ============================================================
// COURSE MODULE QUIZ � Embedded Quiz System
// ============================================================

/**
 * Fetch the published quiz for a given module (student-facing).
 */
const getCourseModuleQuiz = async (moduleId, courseId = null) => {
  try {
    let q;
    if (moduleId) {
      q = query(
        collection(db, 'courseModuleQuizzes'),
        where('moduleId', '==', moduleId),
        where('isPublished', '==', true)
      );
    } else if (courseId) {
      q = query(
        collection(db, 'courseModuleQuizzes'),
        where('courseId', '==', courseId),
        where('moduleId', '==', 'course_quiz'),
        where('isPublished', '==', true)
      );
    } else {
      return null;
    }
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  } catch (err) {
    console.error('getCourseModuleQuiz error:', err);
    return null;
  }
};

/**
 * Admin: get quiz regardless of publish status (for editing).
 */
const getCourseModuleQuizForAdmin = async (moduleId, courseId = null) => {
  try {
    let q;
    if (moduleId) {
      q = query(
        collection(db, 'courseModuleQuizzes'),
        where('moduleId', '==', moduleId)
      );
    } else if (courseId) {
      q = query(
        collection(db, 'courseModuleQuizzes'),
        where('courseId', '==', courseId),
        where('moduleId', '==', 'course_quiz')
      );
    } else {
      return null;
    }
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  } catch (err) {
    console.error('getCourseModuleQuizForAdmin error:', err);
    return null;
  }
};

/**
 * Admin: Create or update a module quiz.
 */
/**
 * Read Module: Get quiz regardless of publish status (for editing).
 */
const getReadModuleQuizForAdmin = async (moduleId) => {
  if (!moduleId) return null;
  try {
    const q = query(
      collection(db, 'readModuleQuizzes'),
      where('moduleId', '==', moduleId)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  } catch (err) {
    console.error('getReadModuleQuizForAdmin error:', err);
    return null;
  }
};

/**
 * Read Module: Create or update a read module quiz.
 */
const saveReadModuleQuiz = async (quizData, quizId = null) => {
  try {
    const payload = { ...quizData, updatedAt: new Date() };
    if (quizId) {
      await updateDoc(doc(db, 'readModuleQuizzes', quizId), payload);
      return { success: true, id: quizId };
    } else {
      payload.createdAt = new Date();
      const ref = await addDoc(collection(db, 'readModuleQuizzes'), payload);
      return { success: true, id: ref.id };
    }
  } catch (err) {
    console.error('saveReadModuleQuiz error:', err);
    return { success: false, message: err.message };
  }
};

/**
 * Read Module Student-Facing: Fetch published quiz.
 */
const getReadModuleQuiz = async (moduleId) => {
  if (!moduleId) return null;
  try {
    const q = query(
      collection(db, 'readModuleQuizzes'),
      where('moduleId', '==', moduleId),
      where('isPublished', '==', true)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const d = snap.docs[0];
      return { id: d.id, ...d.data() };
    }

    // Fallback: query without isPublished filter to prevent silent omissions in local testing
    const qFallback = query(
      collection(db, 'readModuleQuizzes'),
      where('moduleId', '==', moduleId)
    );
    const snapFallback = await getDocs(qFallback);
    if (!snapFallback.empty) {
      const d = snapFallback.docs[0];
      return { id: d.id, ...d.data() };
    }

    return null;
  } catch (err) {
    console.error('getReadModuleQuiz error:', err);
    return null;
  }
};

/**
 * Read Module Student: Save attempt.
 */
const saveReadModuleQuizResult = async (resultData) => {
  try {
    const ref = await addDoc(collection(db, 'readModuleQuizResults'), {
      ...resultData,
      createdAt: new Date()
    });
    return { success: true, id: ref.id };
  } catch (err) {
    console.error('saveReadModuleQuizResult error:', err);
    return { success: false, message: err.message };
  }
};

/**
 * Read Module Student: Get quiz results map.
 */
const getReadModuleQuizResultsForUser = async (userId, courseId) => {
  if (!userId) return {};
  try {
    const q = query(
      collection(db, 'readModuleQuizResults'),
      where('userId', '==', userId)
    );
    const snap = await getDocs(q);
    const map = {};
    snap.docs.forEach(d => {
      const data = { id: d.id, ...d.data() };
      const existing = map[data.moduleId];
      if (!existing || data.percentage > existing.percentage) {
        map[data.moduleId] = data;
      }
    });
    return map;
  } catch (err) {
    console.error('getReadModuleQuizResultsForUser error:', err);
    return {};
  }
};


const saveCourseModuleQuiz = async (quizData, quizId = null) => {
  try {
    const payload = { ...quizData, updatedAt: new Date() };
    if (quizId) {
      await updateDoc(doc(db, 'courseModuleQuizzes', quizId), payload);
      return { success: true, id: quizId };
    } else {
      payload.createdAt = new Date();
      const ref = await addDoc(collection(db, 'courseModuleQuizzes'), payload);
      return { success: true, id: ref.id };
    }
  } catch (err) {
    console.error('saveCourseModuleQuiz error:', err);
    return { success: false, message: err.message };
  }
};

/**
 * Student: Save a quiz attempt result.
 */
const saveModuleQuizResult = async (resultData) => {
  try {
    const ref = await addDoc(collection(db, 'courseModuleQuizResults'), {
      ...resultData,
      createdAt: new Date()
    });
    return { success: true, id: ref.id };
  } catch (err) {
    console.error('saveModuleQuizResult error:', err);
    return { success: false, message: err.message };
  }
};

/**
 * Student: Get all module quiz results for a user in a course.
 * Returns a map { [moduleId]: bestResultDoc } (highest percentage attempt per module).
 */
const getModuleQuizResultsForUser = async (userId, courseId) => {
  if (!userId || !courseId) return {};
  try {
    const q = query(
      collection(db, 'courseModuleQuizResults'),
      where('userId', '==', userId),
      where('courseId', '==', courseId)
    );
    const snap = await getDocs(q);
    const map = {};
    snap.docs.forEach(d => {
      const data = { id: d.id, ...d.data() };
      const existing = map[data.moduleId];
      if (!existing || data.percentage > existing.percentage) {
        map[data.moduleId] = data;
      }
    });
    return map;
  } catch (err) {
    console.error('getModuleQuizResultsForUser error:', err);
    return {};
  }
};

/**
 * Student: After passing a module quiz, record it in the enrollment doc
 * and recalculate overall course progress.
 */
const updateModuleQuizProgressInEnrollment = async (
  userId,
  courseId,
  moduleId,
  totalLessons,
  completedLessonsCount,
  totalModuleQuizzes
) => {
  try {
    const q = query(
      collection(db, 'enrollments'),
      where('userId', '==', userId),
      where('courseId', '==', courseId)
    );
    const snap = await getDocs(q);
    if (snap.empty) return { success: false, message: 'Enrollment not found' };

    const enrollDoc = snap.docs[0];
    const enrollData = enrollDoc.data();
    const existingPassedQuizzes = enrollData.completedModuleQuizzes || [];

    const updatedPassedQuizzes = existingPassedQuizzes.includes(moduleId)
      ? existingPassedQuizzes
      : [...existingPassedQuizzes, moduleId];

    const totalWeight = (totalLessons || 0) + (totalModuleQuizzes || 0);
    const completedWeight = (completedLessonsCount || 0) + updatedPassedQuizzes.length;
    const newProgress = totalWeight > 0
      ? Math.min(100, Math.round((completedWeight / totalWeight) * 100))
      : enrollData.progress || 0;

    await updateDoc(doc(db, 'enrollments', enrollDoc.id), {
      completedModuleQuizzes: updatedPassedQuizzes,
      progress: newProgress,
      lastAccessedAt: new Date(),
      status: newProgress >= 100 ? 'completed' : 'active'
    });

    return { success: true, newProgress };
  } catch (err) {
    console.error('updateModuleQuizProgressInEnrollment error:', err);
    return { success: false, message: err.message };
  }
};

/**
 * Log attendance heartbeat for an ongoing event meeting session
 */
const logAttendanceHeartbeat = async (userId, eventId, eventDurationMinutes = 60) => {
  if (!userId || !eventId) return;
  try {
    const logId = `${userId}_${eventId}`;
    const logRef = doc(db, "eventAttendanceLogs", logId);
    const regRef = doc(db, "eventRegistrations", logId);

    const logSnap = await getDoc(logRef);
    let activeMinutes = 1;
    if (logSnap.exists()) {
      activeMinutes = (logSnap.data().activeMinutes || 0) + 1;
      await updateDoc(logRef, {
        activeMinutes,
        lastHeartbeat: new Date(),
        status: "in_session"
      });
    } else {
      await setDoc(logRef, {
        userId,
        eventId,
        joinedAt: new Date(),
        lastHeartbeat: new Date(),
        activeMinutes: 1,
        status: "in_session"
      });
    }

    const minRequiredMinutes = Math.max(5, Math.floor(eventDurationMinutes * 0.7));
    const isAttended = activeMinutes >= minRequiredMinutes;

    await setDoc(regRef, {
      status: isAttended ? "attended" : "in_session",
      activeMinutes,
      eligibleForCertificate: isAttended,
      lastActiveAt: new Date()
    }, { merge: true });

  } catch (err) {
    console.error("Error logging attendance heartbeat:", err);
  }
};

export { logAttendanceHeartbeat };


